/**
 * Message Authentication
 *
 * Provides cryptographic signing and verification for messages
 * to prevent sender spoofing.
 */

import { signMessage, verifySignature, uint8ArrayToBase64, base64ToUint8Array, derivePeerId } from './identity'
import type { MessageEntry } from '@/lib/sync/crdt'

/**
 * Fields to include in signature (excludes signature itself)
 */
function getSignableContent(message: Omit<MessageEntry, 'signature'>): string {
  // Create deterministic JSON of signable fields
  const signable = {
    id: message.id,
    type: message.type,
    senderId: message.senderId,
    senderName: message.senderName,
    senderRole: message.senderRole,
    timestamp: message.timestamp,
    content: message.content,
    audioData: message.audioData,
    duration: message.duration,
    transcript: message.transcript,
    poiType: message.poiType,
    locationLat: message.locationLat,
    locationLng: message.locationLng,
    address: message.address,
    expiresAt: message.expiresAt,
    senderPublicKey: message.senderPublicKey,
    sequence: message.sequence, // Include sequence number in signature
  }
  // Sort keys for deterministic output
  return JSON.stringify(signable, Object.keys(signable).sort())
}

/**
 * Sign a message with the sender's private key
 */
export async function signMessageEntry(
  message: Omit<MessageEntry, 'signature'>,
  privateKey: Uint8Array,
  publicKey: Uint8Array
): Promise<MessageEntry> {
  // Add public key to message
  const messageWithKey = {
    ...message,
    senderPublicKey: uint8ArrayToBase64(publicKey),
  }

  // Create signable content
  const content = getSignableContent(messageWithKey)
  const contentBytes = new TextEncoder().encode(content)

  // Sign
  const signature = await signMessage(contentBytes, privateKey)

  return {
    ...messageWithKey,
    signature: uint8ArrayToBase64(signature),
  }
}

/**
 * Verify a message signature
 * Returns true if signature is valid, false otherwise
 */
export async function verifyMessageEntry(message: MessageEntry): Promise<boolean> {
  // System messages don't need verification
  if (message.type === 'system') {
    return true
  }

  // Messages without signature are invalid (except legacy messages)
  if (!message.signature || !message.senderPublicKey) {
    console.warn('[MessageAuth] Message missing signature or public key:', message.id)
    return false
  }

  try {
    // Reconstruct signable content (without signature field)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { signature: _sig, ...messageWithoutSig } = message
    const content = getSignableContent(messageWithoutSig)
    const contentBytes = new TextEncoder().encode(content)

    // Decode signature and public key
    const signatureBytes = base64ToUint8Array(message.signature)
    const publicKeyBytes = base64ToUint8Array(message.senderPublicKey)

    // Verify
    const isValid = await verifySignature(signatureBytes, contentBytes, publicKeyBytes)

    if (!isValid) {
      console.warn('[MessageAuth] Invalid signature for message:', message.id)
    }

    return isValid
  } catch (error) {
    console.error('[MessageAuth] Verification error:', error)
    return false
  }
}

/**
 * Verify that the senderId is cryptographically bound to the public key
 * (prevents claiming someone else's messages with your own signature)
 */
export async function verifySenderIdentity(message: MessageEntry): Promise<boolean> {
  if (!message.senderPublicKey) {
    return false
  }

  try {
    // Derive peerId from the public key
    const publicKeyBytes = base64ToUint8Array(message.senderPublicKey)
    const derivedPeerId = await derivePeerId(publicKeyBytes)

    // Verify the senderId matches what we derived from the public key
    const isValid = message.senderId === derivedPeerId

    if (!isValid) {
      console.warn(
        '[MessageAuth] SenderId mismatch! Claimed:',
        message.senderId,
        'Derived:',
        derivedPeerId
      )
    }

    return isValid
  } catch (error) {
    console.error('[MessageAuth] Failed to verify sender identity:', error)
    return false
  }
}
